import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, AreaChart, Area,
} from "recharts";
import {
  ArrowLeftIcon, BrainIcon, GitBranchIcon, GitForkIcon,
} from "@phosphor-icons/react";

const g = (obj, key, fallback = "—") => (obj && obj[key] !== undefined && obj[key] !== null && obj[key] !== "") ? obj[key] : fallback;
const n = (v) => (v === undefined || v === null || v === "") ? 0 : Number(v);

const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };
const TOOLTIP_STYLE = { background: "#15171E", border: "1px solid #2A2D3A", borderRadius: 6, fontSize: 12 };
const AXIS_LINE_STROKE = { stroke: "#2A2D3A" };
const AXIS_TICK = { fontSize: 10, fill: "#64748B" };

export default function BotDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setErr(null);
    setData(null);
    api.get(`/bots/${encodeURIComponent(id)}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.status === 404 ? "Bot not found" : "Failed to load bot"));
  }, [id]);

  if (err) {
    return (
      <div data-testid="bot-detail-page" className="p-6">
        <Link to="/leaderboard" className="text-xs mono uppercase tracking-widest text-[#94A3B8] hover:text-[#D6FF38]">
          <ArrowLeftIcon size={12} className="inline" /> Back to Leaderboard
        </Link>
        <div data-testid="bot-not-found" className="mt-6 border border-[#FF4D4D]/30 bg-[#FF4D4D]/5 rounded-md p-6 text-[#FF4D4D] mono text-sm">
          {err}
        </div>
      </div>
    );
  }

  if (!data) return <div data-testid="bot-detail-page" className="p-6 text-[#64748B] mono text-sm">Loading…</div>;

  const { bot, parent, children = [], decisions = [] } = data;
  const months = Array.isArray(bot.monthly_results) ? bot.monthly_results : [];

  return (
    <div data-testid="bot-detail-page" className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto">
      <Link to="/leaderboard" data-testid="back-to-leaderboard" className="inline-flex items-center gap-2 text-xs mono uppercase tracking-widest text-[#94A3B8] hover:text-[#D6FF38]">
        <ArrowLeftIcon size={12} /> Back to Leaderboard
      </Link>

      {/* Header */}
      <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">
              {g(bot, "symbol")} · {g(bot, "verdict", "—")}
            </div>
            <h1 className="text-2xl md:text-3xl tracking-tight font-medium mt-1 mono break-all">{g(bot, "name")}</h1>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <StatusBadge status={g(bot, "status", "testing")} />
              <span className="text-[11px] mono text-[#94A3B8]">{g(bot, "verdict")}</span>
              {bot.strategy_family && (
                <span
                  data-testid="bot-strategy-family"
                  className="text-[10px] mono uppercase tracking-widest border border-[#D6FF38]/30 bg-[#D6FF38]/5 text-[#D6FF38] rounded-sm px-2 py-0.5"
                  title={g(bot, "strategy_family_label", bot.strategy_family)}
                >
                  {bot.strategy_family} · {g(bot, "strategy_family_label", bot.strategy_family)}
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 text-right">
            <div>
              <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Final Balance</div>
              <div className="mono text-xl md:text-2xl mt-1">{fmt.money(n(bot.final_balance))}</div>
              <div className="text-[11px] mono text-[#94A3B8]">from {fmt.money(n(bot.starting_balance))}</div>
            </div>
            <div>
              <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Total Growth</div>
              <div className="mono text-xl md:text-2xl mt-1" style={{ color: n(bot.growth_pct) >= 0 ? "#2ECA7F" : "#FF4D4D" }}>
                {fmt.signed(n(bot.growth_pct))}%
              </div>
              {/* v6.5.43: "net" is final - start, NOT the stale `bot.growth`
                  field (which held raw MonthlyGrowth% on some bridge rows). */}
              <div className="text-[11px] mono text-[#94A3B8]" data-testid="bot-net-profit">
                {fmt.signed(n(bot.final_balance) - n(bot.starting_balance))} net
              </div>
            </div>
            <div className="col-span-2 md:col-span-1">
              <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Months Tested</div>
              <div className="mono text-xl md:text-2xl mt-1 text-[#D6FF38]">{g(bot, "months_tested", 0)}</div>
              <div className="text-[11px] mono text-[#94A3B8]">{g(bot, "profit_months", g(bot, "profitable_months", 0))} profitable</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-2 md:gap-3">
        {[
          ["Months", g(bot, "months_tested", 0)],
          ["Win-Mo %", `${g(bot, "profit_month_pct", 0)}%`],
          ["Avg/Mo", `${fmt.signed(n(bot.average_month))}`, n(bot.average_month) >= 0 ? "#2ECA7F" : "#FF4D4D"],
          ["Best Mo", fmt.money(n(bot.best_month)), "#2ECA7F"],
          ["Worst Mo", fmt.money(n(bot.worst_month)), "#FF4D4D"],
          ["Balance DD", `${g(bot, "balance_dd", g(bot, "max_drawdown", 0))}%`, "#FF4D4D"],
          ["Equity DD", `${g(bot, "equity_dd", 0)}%`, "#F97316"],
          ["Profit Factor", g(bot, "profit_factor", 0)],
          ["Net Profit", fmt.money(n(bot.final_balance) - n(bot.starting_balance))],
        ].map(([label, val, color]) => (
          <div key={label} className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-3">
            <div className="text-[10px] mono uppercase tracking-widest text-[#94A3B8]">{label}</div>
            <div className="mono text-lg mt-1" style={{ color: color || "#F1F5F9" }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Trading activity + risk row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        {(() => {
          const totalTrades = n(bot.total_trades);
          const wins = n(bot.winning_trades);
          const losses = n(bot.losing_trades);
          const wr = n(bot.overall_win_rate);
          const sharpe = n(bot.sharpe);
          const avgTradesPerMonth = bot.months_tested > 0 ? (totalTrades / bot.months_tested) : 0;
          const cards = [
            {
              label: "Total Trades",
              val: totalTrades > 0 ? totalTrades : "—",
              sub: totalTrades > 0 ? `${wins}W / ${losses}L` : "no trade data",
              accent: "#D6FF38",
            },
            {
              label: "Trades / Month",
              val: avgTradesPerMonth > 0 ? avgTradesPerMonth.toFixed(1) : "—",
              sub: totalTrades > 0 ? "average" : "requires trade data",
              accent: "#60A5FA",
            },
            {
              label: "Win Rate",
              val: wr > 0 ? `${wr.toFixed(1)}%` : "—",
              sub: wr > 0 ? (wr >= 55 ? "healthy edge" : wr >= 45 ? "marginal edge" : "unreliable") : "no trades logged",
              accent: wr >= 55 ? "#2ECA7F" : wr >= 45 ? "#FFB400" : "#94A3B8",
            },
            {
              label: "Sharpe (Annual)",
              val: sharpe !== 0 ? sharpe.toFixed(2) : "—",
              sub: sharpe >= 2 ? "excellent" : sharpe >= 1 ? "good" : sharpe > 0 ? "weak" : "n/a",
              accent: sharpe >= 2 ? "#2ECA7F" : sharpe >= 1 ? "#D6FF38" : "#94A3B8",
            },
          ];
          return cards.map((c) => (
            <div
              key={c.label}
              className="relative border border-[#2A2D3A] bg-[#15171E] rounded-md p-3 overflow-hidden"
              data-testid={`bot-stat-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="absolute inset-y-0 left-0 w-[3px]" style={{ background: c.accent }} />
              <div className="text-[10px] mono uppercase tracking-widest text-[#94A3B8]">{c.label}</div>
              <div className="mono text-xl mt-1" style={{ color: c.accent }}>{c.val}</div>
              <div className="text-[10px] mono text-[#64748B] mt-0.5">{c.sub}</div>
            </div>
          ));
        })()}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="space-y-3">
          <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
            <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-3 flex items-center gap-2">
              <GitBranchIcon size={12} /> Lineage
            </div>
            {parent ? (
              <div>
                <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Parent</div>
                <Link to={`/bot/${encodeURIComponent(parent.id)}`} data-testid="lineage-parent" className="block mono text-sm hover:text-[#D6FF38] break-all">
                  ← {parent.name} · {fmt.signed(n(parent.growth_pct))}%
                </Link>
              </div>
            ) : (
              <div className="text-xs text-[#64748B] mono">Gen 0 — root bot</div>
            )}
            <div className="mt-3 text-[10px] mono uppercase tracking-widest text-[#64748B] flex items-center gap-1">
              <GitForkIcon size={12} /> Mutations ({children.length})
            </div>
            <div className="space-y-1 mt-1 max-h-40 overflow-y-auto">
              {children.length === 0 && <div className="text-xs text-[#64748B] mono">No offspring yet</div>}
              {children.map((c) => (
                <Link key={c.id} to={`/bot/${encodeURIComponent(c.id)}`} className="flex items-center justify-between text-xs py-1 hover:text-[#D6FF38] gap-2">
                  <span className="mono truncate">→ {c.name}</span>
                  <span className="mono whitespace-nowrap" style={{ color: n(c.growth_pct) >= 0 ? "#2ECA7F" : "#FF4D4D" }}>
                    {fmt.signed(n(c.growth_pct))}%
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-3">
          <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
            <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-2">Monthly P&L</div>
            {months.length === 0 ? (
              <div className="h-40 flex items-center justify-center text-[#64748B] mono text-xs">
                No per-month data yet. Run bridge_agent.py v6.3+ to sync Compound_Results.csv.
              </div>
            ) : (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={months} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="2 4" stroke="#2A2D3A" opacity={0.3} vertical={false} />
                    <XAxis dataKey="month" tickLine={false} axisLine={AXIS_LINE_STROKE} tick={AXIS_TICK} />
                    <YAxis tickLine={false} axisLine={false} width={48} tick={AXIS_TICK} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => fmt.money(Number(v))} />
                    <Bar dataKey="profit" radius={[3, 3, 0, 0]}>
                      {months.map((m) => <Cell key={`${m.year}-${m.month}`} fill={n(m.profit) >= 0 ? "#2ECA7F" : "#FF4D4D"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
            <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-2">Compounding Equity Curve</div>
            {months.length === 0 ? (
              <div className="h-40 flex items-center justify-center text-[#64748B] mono text-xs">
                No equity curve yet.
              </div>
            ) : (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={months} margin={CHART_MARGIN}>
                    <defs>
                      <linearGradient id="gBot" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D6FF38" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#D6FF38" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="2 4" stroke="#2A2D3A" opacity={0.3} />
                    <XAxis dataKey="month" tickLine={false} axisLine={AXIS_LINE_STROKE} tick={AXIS_TICK} />
                    <YAxis tickLine={false} axisLine={false} width={56} tick={AXIS_TICK} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => fmt.money(Number(v))} />
                    <Area type="monotone" dataKey="final_balance" stroke="#D6FF38" strokeWidth={2} fill="url(#gBot)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {months.length > 0 && (
            <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4 overflow-x-auto">
              <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-2">Monthly Breakdown</div>
              <table className="w-full text-sm" data-testid="monthly-breakdown">
                <thead>
                  <tr
                    className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/90 border-b border-[#D6FF38]/20"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(214,255,56,0.08) 0%, rgba(214,255,56,0.02) 100%)",
                    }}
                  >
                    <th className="text-left py-2 font-normal px-2">Month</th>
                    <th className="text-right py-2 font-normal">Start $</th>
                    <th className="text-right py-2 font-normal">End $</th>
                    <th className="text-right py-2 font-normal">P&L</th>
                    <th className="text-right py-2 font-normal">Growth %</th>
                    <th className="text-right py-2 font-normal">Trades</th>
                    <th className="text-right py-2 font-normal">WR %</th>
                    <th className="text-right py-2 font-normal">Bal DD</th>
                    <th className="text-right py-2 font-normal px-2">Eq DD</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => {
                    const trades = n(m.trades);
                    const wr = n(m.win_rate);
                    const bdd = n(m.balance_dd);
                    const edd = n(m.equity_dd);
                    return (
                      <tr key={`${m.year}-${m.month}`} className="border-b border-[#2A2D3A]/30">
                        <td className="py-2 mono text-[#F1F5F9] px-2">{m.month}</td>
                        <td className="py-2 mono text-right text-[#94A3B8]">{fmt.money(n(m.starting_balance))}</td>
                        <td className="py-2 mono text-right">{fmt.money(n(m.final_balance))}</td>
                        <td className="py-2 mono text-right" style={{ color: n(m.profit) >= 0 ? "#2ECA7F" : "#FF4D4D" }}>
                          {n(m.profit) >= 0 ? "+" : ""}{n(m.profit).toFixed(2)}
                        </td>
                        <td className="py-2 mono text-right" style={{ color: n(m.growth_pct) >= 0 ? "#2ECA7F" : "#FF4D4D" }}>
                          {fmt.signed(n(m.growth_pct))}%
                        </td>
                        <td className="py-2 mono text-right text-[#F1F5F9]">{trades > 0 ? trades : "—"}</td>
                        <td className="py-2 mono text-right text-[#94A3B8]">{wr > 0 ? `${wr.toFixed(1)}%` : "—"}</td>
                        <td className="py-2 mono text-right text-[#FF4D4D]">{bdd > 0 ? `${bdd.toFixed(2)}%` : "—"}</td>
                        <td className="py-2 mono text-right text-[#F97316] px-2">{edd > 0 ? `${edd.toFixed(2)}%` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* AI judgment log */}
      <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
        <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-3 flex items-center gap-2">
          <BrainIcon size={12} /> AI Judgment History
        </div>
        {decisions.length === 0 && <div className="text-xs text-[#64748B] mono">No decisions recorded yet</div>}
        <div className="space-y-3">
          {decisions.map((d, i) => (
            <div key={d.id || i} className="border-l-2 border-[#D6FF38]/40 pl-3" data-testid={`bot-decision-${d.id || i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]">{g(d, "decision_type", "DECISION")}</span>
                <span className="text-[10px] mono text-[#64748B]">{d.timestamp ? new Date(d.timestamp).toLocaleString() : ""}</span>
              </div>
              {d.title && <div className="text-sm mt-1 text-[#F1F5F9]">{d.title}</div>}
              {d.reasoning && <div className="text-xs mt-1 text-[#94A3B8] leading-relaxed whitespace-pre-wrap">{d.reasoning}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
